import { FC, useMemo, useState } from 'react';

import { AnalyticsDayLabels, AnalyticsDayPoint } from './analyticsDay.types';
import { DistributionRow } from './AnalyticsDistributionRow';
import { describeDistribution } from './analyticsDistribution.util';
import { formatRate } from './adminBugoStat.util';

//* 차트 라이브러리를 쓰지 않는 저장소 관례에 맞춰 div 막대로 그린다.
//* 날짜가 많으면 가로 스크롤 — 모바일에서 막대가 실처럼 얇아지는 것을 막는다.
//* 지표 이름은 labels로 받아 부고/화환/답례품 탭이 같은 컴포넌트를 쓴다.

const MIN_BAR_WIDTH_PX = 24;

export const AnalyticsDailyTrend: FC<{
  title: string;
  days: AnalyticsDayPoint[];
  labels: AnalyticsDayLabels;
}> = function AnalyticsDailyTrend({ title, days, labels }) {
  const [hovered, setHovered] = useState<AnalyticsDayPoint | null>(null);

  const max = useMemo(
    () => days.reduce((acc, day) => Math.max(acc, day.value), 0),
    [days],
  );

  const totals = useMemo(
    () =>
      days.reduce(
        (acc, day) => ({
          value: acc.value + day.value,
          sub: acc.sub + (day.subValue ?? 0),
        }),
        { value: 0, sub: 0 },
      ),
    [days],
  );

  //* 평균·중앙값·최저/최다·편차 — 값이 있었던 날 기준
  const dist = useMemo(() => describeDistribution(days), [days]);

  if (days.length === 0) return null;

  const withSub = Boolean(labels.sub);
  const tooltipOf = (day: AnalyticsDayPoint) =>
    `${day.date} · ${labels.value} ${day.value}${labels.unit}${
      withSub ? ` · ${labels.sub} ${day.subValue ?? 0}${labels.unit}` : ''
    }`;

  return (
    <div className="rounded-lg bg-white p-3 shadow-smd">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <p className="text-16 font-bold text-gray-900">{title}</p>
        <p className="text-12 tabular-nums text-gray-600">
          {hovered ? tooltipOf(hovered) : `최대 ${max.toLocaleString()}${labels.unit}`}
        </p>
      </div>

      <div className="mt-2 overflow-x-auto">
        <div
          className="flex items-end gap-0.5"
          style={{ minWidth: days.length * MIN_BAR_WIDTH_PX }}
        >
          {days.map((day) => {
            const height = max > 0 ? (day.value / max) * 100 : 0;
            //* 부분값은 전체 막대 안쪽 비율로 그린다
            const subHeight = day.value > 0 ? ((day.subValue ?? 0) / day.value) * 100 : 0;
            return (
              <div
                key={day.date}
                className="flex flex-1 flex-col items-center"
                onMouseEnter={() => setHovered(day)}
                onMouseLeave={() => setHovered(null)}
              >
                <div
                  className="flex w-full items-end justify-center"
                  style={{ height: 140 }}
                  title={tooltipOf(day)}
                >
                  {/* 값이 0보다 크면 최소 높이를 줘서 "있긴 있다"가 보이게 한다 */}
                  <div
                    className="relative w-full rounded-t bg-indigo-200"
                    style={{ height: `${Math.max(height, day.value > 0 ? 3 : 0)}%` }}
                  >
                    {withSub && (
                      <div
                        className="absolute bottom-0 left-0 w-full rounded-t bg-indigo-600"
                        style={{ height: `${subHeight}%` }}
                      />
                    )}
                  </div>
                </div>
                <p className="mt-1 text-11 tabular-nums leading-4 text-gray-500">
                  {Number(day.date.slice(8, 10))}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-12 text-gray-600">
        {withSub && (
          <>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-600" />
              {labels.sub}
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-200" />그 외
            </span>
          </>
        )}
        <span className="ml-auto tabular-nums">
          기간 합계 {totals.value.toLocaleString()}
          {labels.unit}
          {withSub && ` · ${labels.sub}율 ${formatRate(totals.sub, totals.value)}`}
        </span>
      </div>

      {dist && (
        <DistributionRow
          stats={dist}
          labels={labels}
          format={(value) => value.toLocaleString()}
        />
      )}
    </div>
  );
};
